// import SignatureCanvas from 'react-signature-canvas';
import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getkyc,
  updatekyc,
  uploadFile,
  generateFormToken,
  generateShareLink,
  notifyEmail,
  sendEmailNotification,
  createNotification,
  frontEndBaseURL,
  makeTinyUrl,
} from '../../../API/Api';
import { toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import FormStatusEnum from '../../../data/FormStatusEnum';
import CountryList from './CountryList';
import CounterpartyManager from './CounterpartyManager';
import { Tooltip } from '@mui/material';
import { DateTime } from 'luxon';
import Modal from '../../shared/Modal';
import { TokenContext } from '../../../App';
import CustomTooltip from './CustomTooltip';
import { encrypt, decrypt } from '../../shared/utils';

const _IndividualDetails = ({
  individualName,
  showSubmitButton,
  clientFormId,
  readOnly,
  currentUrl,
  maxPage,
  token,
  formAuthority,
}) => {
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [formData, setFormData] = useState({
    _id: clientFormId,
    name: '',
    gender: '',
    identificationNumber: '',
    identificationType: '',
    identificationDocumentLink: '',
    identificationExpiryDate: '',
    contactNumber: '',
    email: '',
    dateOfBirth: '',
    address: '',
    sourceOfWealth: '',
    residenceCountry: 'SG',
    nationality: 'SG',
    occupation: '',
    formStatus: '',
    addressProofURL: '',
    emailSent: '',
    isvVideo: 'no',
    // secondNationality: '',
    pepPerson: 'No',
    pepPersonCountryOrOrganisation: '',
    pepPersonPublicFunctionNature: '',
    pepPersonRoleFrom: '',
    pepPersonRoleTo: '',
    pepAssociate: 'No',
    pepAssociateRelationship: '',
    pepAssociateName: '',
    pepAssociateCountryOrOrganisation: '',
    pepAssociatePublicFunctionNature: '',
    pepAssociateRoleFrom: '',
    pepAssociateRoleTo: '',
    pepFamily: 'No',
    pepFamilyRelationship: '',
    pepFamilyName: '',
    pepFamilyCountryOrOrganisation: '',
    pepFamilyPublicFunctionNature: '',
    pepFamilyRoleFrom: '',
    pepFamilyRoleTo: '',
    declarationName: '',
    declarationDate: '',
    signatureUrl: '',
    accountManager: '',
    businessRelationShip: '',
    notApplicable: false,
    otherDocuments: '',
    notes: '',
  }); //How do you setState in nested field
  // TODO: DB call for businessRelationShip
  const [mandatoryFieldError, setMandatoryFieldError] = useState({
    name: '',
    address: '',
    gender: '',
    identificationNumber: '',
    identificationType: '',
    email: '',
    dateOfBirth: '',
    residenceCountry: '',
    nationality: '',
    addressProofURL: '',
    pepPerson: '',
    pepAssociate: '',
    pepFamily: '',
    businessRelationShip: '',
    identificationExpiryDate: '',
  });

  const decodedToken = jwtDecode(token);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const navigate = useNavigate();
  useEffect(() => {
    const fetchData = async () => {
      const userData = await getkyc(clientFormId, token);
      setFormData({
        _id: clientFormId,
        name: userData.data.name,
        email: userData.data.email,
        formStatus: userData.data.formStatus,
        maxPage: userData.data.maxPage,
        ...userData.data?.details,
        residenceCountry: userData.data?.details?.residenceCountry || 'SG',
        nationality: userData.data?.details?.nationality || 'SG',
        isvVideo: userData.data?.details?.isvVideo,
        counterparties: userData?.data?.details?.counterparties || [],
      });
      // signatureRef = useRef()
      setIsDataLoaded(true); // 数据加载完成
    };
    fetchData();
  }, []);

  const handleFileUpload = async (e, fileType) => {
    const { name } = e.target;
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    try {
      const data = new FormData();
      data.append('file', file);
      const filename = `${fileType}_${file.name}`;

      const res = await uploadFile(data, filename, token);

      if (res) {
        setFormData(prevState => {
          const currentValue = prevState[name];

          // 如果是 otherDocuments 字段，支持数组和字符串两种格式
          if (name === 'otherDocuments') {
            if (Array.isArray(currentValue)) {
              // 如果已经是数组，添加到数组中
              return {
                ...prevState,
                [name]: [...currentValue, res.data.url],
              };
            } else if (currentValue) {
              // 如果已经有值但不是数组，转换为数组
              return {
                ...prevState,
                [name]: [currentValue, res.data.url],
              };
            } else {
              // 如果没有值，直接设置为字符串（保持向后兼容）
              return {
                ...prevState,
                [name]: res.data.url,
              };
            }
          } else {
            // 其他字段保持原有逻辑
            return {
              ...prevState,
              [name]: res.data.url,
              // identificationDocumentLink :  res.data.url // name refer to 'input name' property.
            };
          }
        });
      } else {
        toast.error('Invalid file type!');
      }
    } catch (e) {
      toast.error('Invalid file type!');
    }
  };

  const identificationDocumentLinkRef = useRef(null);
  const addressProofURLRef = useRef(null);

  const handleFileRemove = async (fileName, ref) => {
    if (readOnly) {
      return;
    }
    setFormData(prevState => ({
      ...prevState,
      [fileName]: '',
    }));

    if (ref && ref.current) {
      ref.current.value = '';
    }
  };

  const handleFileRemoveFromArray = async (fileName, index) => {
    if (readOnly) {
      return;
    }

    setFormData(prevState => {
      const currentValue = prevState[fileName];
      if (Array.isArray(currentValue)) {
        const newArray = currentValue.filter((_, i) => i !== index);
        return {
          ...prevState,
          [fileName]: newArray.length === 0 ? '' : newArray,
        };
      }
      return prevState;
    });
  };

  // Counterparty management functions
  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value, // name refer to 'input name' property.
    }));
    if (name === 'notApplicable') {
      if (value == true) {
        setFormData(prevState => ({
          ...prevState,
          identificationExpiryDate: '9999-12-31',
        }));
      } else {
        setFormData(prevState => ({
          ...prevState,
          identificationExpiryDate: '',
        }));
      }
    }
    const requiredFields = [
      'name',
      'address',
      'gender',
      'identificationNumber',
      'identificationType',
      'email',
      'dateOfBirth',
      'residenceCountry',
      'nationality',
      'addressProofURL',
      'pepPerson',
      'pepAssociate',
      'pepFamily',
      'identificationExpiryDate',
      'identificationDocumentLink',
    ];
    if (formAuthority === 'law') {
      requiredFields.push('businessRelationShip');
    }
    if (!requiredFields.includes(name)) {
      return;
    }

    let newMandatoryFieldError = { ...mandatoryFieldError };
    if (value.trim() === '') {
      newMandatoryFieldError[name] = true;
    } else {
      newMandatoryFieldError[name] = false;
    }

    setMandatoryFieldError(newMandatoryFieldError);
  };

  // Handle counterparty changes from CounterpartyManager
  const handleCounterpartiesChange = useCallback(({ counterparties, enableCounterparties }) => {
    // console.log('counterparties', counterparties);
    setFormData(prevState => ({
      ...prevState,
      counterparties,
      enableCounterparties,
    }));
  }, []);
  // const handleClearSignature = () => { signatureRef.current.clear(); setFormData(newData) };

  // Backend stores individual form fields in nested "details"; build payload so nationality etc. persist
  const buildPayloadWithDetails = data => {
    const rootKeys = ['_id', 'name', 'email', 'formStatus', 'maxPage'];
    const detailsPayload = {};
    Object.keys(data).forEach(key => {
      if (!rootKeys.includes(key)) {
        detailsPayload[key] = data[key];
      }
    });
    return { ...data, details: detailsPayload };
  };

  const handleSaveForm = async e => {
    // 保存不做校验
    // let signatureData = await signatureRef.current.toDataURL(); // Use the signature data (base64 image) as needed
    const newFormData = {
      ...formData,
      maxPage: formData.maxPage || 1,
      emailSent: false,
      identificationExpiryDate: formData.notApplicable ? '9999-12-31' : formData.identificationExpiryDate,
      counterparties: formData.counterparties || [],
      enableCounterparties: formData.enableCounterparties || 'no',
    };
    setFormData(newFormData);

    const res = await updatekyc(buildPayloadWithDetails(newFormData), queryParams.get('formToken'));
    if (res && res.status === 200 && res.data.status) {
      if (res.data.success) {
        toast.success('Form Saved!');
      } else {
        toast.warning('Form already saved!');
      }
      // const uuid = queryParams.get('_d');
      // if (uuid) {
      //   notifyEmail(uuid, formData);
      // }
    } else {
      toast.error('Failed to save form');
    }
  };

  const generateFormLink = async () => {
    const formTokenData = {
      name: formData.name,
      userId: clientFormId,
      companyId: decodedToken.companyId,
    };

    // const formToken = (await generateFormToken(formTokenData)).data.accessToken;
    // const userVar = `userName=${decodedToken.name ? encrypt(decodedToken.name) : ''}&userEmail=${decodedToken.email ? encrypt(decodedToken.email) : ''}`;
    // const linkToForm = `${frontEndBaseURL}/form/kyc-aml?${userVar}&clientFormId=${clientFormId}&page=1&formToken=${formToken}`;
    const shortLinkToCopy = await generateShareLink({
      formTokenData,
      frontEndBaseURL,
      clientFormId,
    });
    // const uuid = shortLinkToCopy.data?.split('_d=')[1];
    // return `${frontEndBaseURL}/form/kyc-aml?clientFormId=${clientFormId}&page=1&formToken=${formToken}&_d=${uuid}`;
    // const shortLink = `${process.env.REACT_APP_BACKEND_URL}/kyc/kyc-aml?_d=${uuid}`;
    // return (await makeTinyUrl(shortLink)).data;
    return shortLinkToCopy.data;
  };

  const handleSendEmail = async () => {
    // const formTokenData = {
    //   name: formData.name,
    //   userId : clientFormId,
    //   companyId : decodedToken.companyId,
    // }
    // const formToken = (await generateFormToken(formTokenData,  queryParams.get('formToken'))).data.accessToken
    // const linkToForm = frontEndBaseURL + '/form/kyc-aml?clientFormId=' + clientFormId + '&page=1&formToken=' + formToken;

    const linkToForm = await generateFormLink();

    sendEmailNotification({
      email: formData.email.split('/')[0],
      subject: 'KYC Form Invitation - iCompass',
      message: `
        <div style="max-width: 600px; margin: 0 auto; border: 2px solid #FF8C00; padding: 20px; text-align: center;">
          <h1 style="margin-bottom: 20px; text-decoration: underline;">KYC Information Request</h1>
          <p> Dear ${formData.name} (Individual) </p>
          <p> ${decodedToken.name} from ${decodedToken.companyName} has requested for you to fill in your details in order to fulfill their KYC/AML obligations.  </p>
          <p> This link is only valid for Two weeks. </p>
          <p>
            Please use the <a href= '${linkToForm} '> link </a>
            to access the form and fill in the necessary information as requested.
          </p>
          <img src="https://icompass-backend-dev-fhagh5hccpf2ebh9.southeastasia-01.azurewebsites.net/images/from_icompass" alt="iCOMPASS logo" style="display: block; margin: 20px auto; width: 200px; height: auto; max-width: 100%;">
          <p style="font-style: italic; margin-top: 20px;">Please do not reply to this email</p>
        </div> `,
    });

    const newFormData = {
      ...formData,
      emailSent: true,
      maxPage: 1,
      counterparties: formData.counterparties,
    };
    setFormData(newFormData);
    await updatekyc(buildPayloadWithDetails(newFormData), queryParams.get('formToken')); //catch
    toast.info(`Form saved, Email notification to ${formData.email} sent!`);
    navigate('/kyc-aml/customer-list');
  };

  const handleSubmitForm = async e => {
    // const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    // setIsValidEmail(emailPattern.test(formData.email));  // Check if the entered email matches the email pattern
    //   for (const key in formData) {
    //         // if (formData[key].trim() === '') {
    //         //     notifyMissing()
    //         //     return
    //         // }
    // }

    // 1. check mandatory fields
    const requiredFields = [
      'name',
      'address',
      'gender',
      'identificationNumber',
      'identificationType',
      'email',
      'dateOfBirth',
      'residenceCountry',
      'nationality',
      'addressProofURL',
      'pepPerson',
      'pepAssociate',
      'pepFamily',
      'identificationExpiryDate',
      'identificationDocumentLink',
    ];
    if (formAuthority === 'law') {
      requiredFields.push('businessRelationShip');
    }
    let allRequiredFieldsFilled = true;
    let newMandatoryFieldError = { ...mandatoryFieldError };

    requiredFields.forEach(field => {
      if (!formData[field]) {
        allRequiredFieldsFilled = false;
        newMandatoryFieldError[field] = true;
      } else {
        newMandatoryFieldError[field] = false;
      }
    });
    setMandatoryFieldError(newMandatoryFieldError);

    if (!allRequiredFieldsFilled) {
      toast.warning('Please fill in the required fields');
      return;
    }

    // 2. write to DB (include details so backend persists nationality etc.)
    const newFormData = {
      ...formData,
      formStatus: FormStatusEnum.PENDING_RISK_ASSESSMENT_2,
      maxPage: 2,
      emailSent: false,
      counterparties: formData.counterparties,
    };
    setFormData(newFormData);
    toast.info('Form Submitted!');
    await updatekyc(buildPayloadWithDetails(newFormData));
    // await new Promise(r => setTimeout(r, 200));

    await createNotification({
      type: 'KYC',
      text: `${newFormData.name} status has changed to Pending Risk Assessment`,
      date: DateTime.now().toFormat('dd-MM-yyyy HH:mm:ss'),
    });

    if (currentUrl !== '/form/kyc-aml') {
      window.location.href = `/kyc-aml/form?clientFormId=${clientFormId}&page=2`;
      // navigate('/kyc-aml/customer-list')
    } else {
      window.location.reload();
    }
  };

  const formatDateToString = date => {
    if (!date) return '';
    return DateTime.fromISO(date).toFormat('yyyy-MM-dd');
  };

  return (
    <div className='max-w-6xl mx-auto'>
      <div className={`max-w-6xl mx-auto mt-3 p-5 rounded-lg shadow-lg ${readOnly ? 'bg-gray-200' : 'bg-white '}`}>
        {/* 1st row */}
        <div className='grid grid-cols-2 gap-4 mb-4'>
          <div>
            <label htmlFor='fullName' className='block font-medium text-gray-700 mb-1'>
              Full Name<i className='text-red-600'>*</i>
            </label>
            <input
              maxLength={800}
              disabled={readOnly}
              type='text'
              id='fullName'
              name='name'
              placeholder='Enter Full Name'
              value={formData.name}
              onChange={handleInputChange}
              className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
            />
            {mandatoryFieldError.name && <p style={{ color: 'red' }}>Please enter name</p>}
          </div>
          <div>
            <label className='block font-medium text-gray-700 mb-1'>
              Gender<i className='text-red-600'>*</i>
            </label>
            <div className='mt-1 flex items-center'>
              <input
                maxLength={800}
                disabled={readOnly}
                type='radio'
                id='male'
                name='gender'
                value='male'
                checked={formData.gender === 'male'}
                onChange={handleInputChange}
                className='mr-1 w-4 h-4'
              />
              <label htmlFor='male' className='mr-3'>
                Male
              </label>
              <input
                maxLength={800}
                disabled={readOnly}
                type='radio'
                id='female'
                name='gender'
                value='female'
                checked={formData.gender === 'female'}
                onChange={handleInputChange}
                className='mr-1 w-4 h-4'
              />
              <label htmlFor='female'>Female</label>
              {/* TODO custom radio button  */}
            </div>
            {mandatoryFieldError.gender && <p style={{ color: 'red' }}>Please enter gender</p>}
          </div>
        </div>
        {/* 2nd row */}
        <div className='grid grid-cols-2 gap-4 mb-4'>
          <div>
            <label htmlFor='identificationNumber' className='block font-medium text-gray-700 mb-1'>
              Identification Number <i className='text-red-600'>*</i>{' '}
            </label>
            <input
              maxLength={800}
              disabled={readOnly}
              type='text'
              id='identificationNumber'
              name='identificationNumber'
              placeholder='Enter Identification Number'
              value={formData.identificationNumber}
              onChange={handleInputChange}
              className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
            />
            {mandatoryFieldError.identificationNumber && (
              <p style={{ color: 'red' }}>Please enter Identification Number</p>
            )}
          </div>
          <div>
            <label htmlFor='typeOfIdentification' className='block font-medium text-gray-700 mb-1'>
              Type of Identification<i className='text-red-600'>*</i>
            </label>
            <select
              disabled={readOnly}
              id='typeOfIdentification'
              value={formData.identificationType}
              name='identificationType'
              onChange={handleInputChange}
              className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500'
            >
              <option value=''> - Please Select - </option>
              <option value='passport'>Passport</option>
              <option value='ic'>IC</option>
              <option value='drivingLicense'>Driving License</option>
            </select>
            {mandatoryFieldError.identificationType && (
              <p style={{ color: 'red' }}>Please select identification type</p>
            )}
          </div>
        </div>
        {/* 3rd row */}
        <div className='grid grid-cols-2 gap-4 mb-4'>
          <div>
            <label htmlFor='expiryDate' className='block font-medium text-gray-700 mb-1'>
              Expiry Date of Identification Document<i className='text-red-600'>*</i>
            </label>
            <input
              maxLength={800}
              disabled={readOnly || formData.notApplicable}
              type='date'
              value={formData.identificationExpiryDate}
              name='identificationExpiryDate'
              onChange={handleInputChange}
              className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
              style={formData.notApplicable ? { color: 'transparent' } : {}}
            />
            <label className='mt-4 flex items-center'>
              <span className='block font-medium text-gray-700 mr-1'>Tick if not applicable</span>
              <input
                type='checkbox'
                disabled={readOnly}
                checked={formData.notApplicable}
                onChange={e => {
                  handleInputChange({
                    target: {
                      name: 'notApplicable',
                      value: e.target.checked,
                    },
                  });
                }}
                className='w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500'
              />
            </label>
            {mandatoryFieldError.identificationExpiryDate && (
              <p style={{ color: 'red' }}>Please fill in the expiration date of your identity document</p>
            )}
          </div>
          <div>
            <label htmlFor='fileInput' className='block font-medium text-gray-700 mb-1.5'>
              Upload Identification<i className='text-red-600'>*</i>
            </label>
            {formData.identificationDocumentLink && (
              <>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/get-upload?fileName=${formData.identificationDocumentLink.split('/').slice(-1)[0]}&accessToken=${token}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-600 mr-2'
                >
                  {decodeURIComponent(formData.identificationDocumentLink.split('/').slice(-1)[0])}
                </a>
                <span
                  className='cursor-pointer'
                  onClick={() => handleFileRemove('identificationDocumentLink', identificationDocumentLinkRef)}
                >
                  {' '}
                  ❌{' '}
                </span>
              </>
            )}
            <input
              maxLength={800}
              disabled={readOnly}
              ref={identificationDocumentLinkRef}
              type='file'
              id='fileInput'
              name='identificationDocumentLink'
              onChange={e => handleFileUpload(e, 'IdLink')}
            />
            {mandatoryFieldError.identificationDocumentLink && (
              <p style={{ color: 'red' }}>Please upload identification</p>
            )}
          </div>
        </div>
        {/* 4th row */}
        <div className='grid grid-cols-2 gap-4 mb-4'>
          <div>
            <label htmlFor='email' className='block font-medium text-gray-700 mb-1'>
              Email<i className='text-red-600'>*</i>
            </label>
            <input
              maxLength={800}
              disabled={readOnly}
              type='email'
              id='email'
              placeholder='Enter Email'
              value={formData.email}
              onChange={handleInputChange}
              name='email'
              className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
            />
            {!isValidEmail && <p style={{ color: 'red' }}>Please enter a valid email address</p>}
            {mandatoryFieldError.email && <p style={{ color: 'red' }}>Please enter email</p>}
          </div>
          {decodedToken.companyId == 'Inspro_Insurance' && (
            <div>
              <label htmlFor='contactNumber' className='block font-medium text-gray-700 mb-1'>
                Contact Number
              </label>
              <input
                maxLength={800}
                disabled={readOnly}
                type='tel'
                id='contactNumber'
                placeholder='Enter Contact Number'
                name='contactNumber'
                value={formData.contactNumber}
                onChange={handleInputChange}
                className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
              />
            </div>
          )}
        </div>
        {/* 5th row */}
        <div className='grid grid-cols-2 gap-4 mb-4'>
          <div>
            <label htmlFor='dob' className='block font-medium text-gray-700 mb-1'>
              Date of Birth<i className='text-red-600'>*</i>
            </label>
            <input
              maxLength={800}
              disabled={readOnly}
              type='date'
              id='dob'
              value={formData.dateOfBirth}
              onChange={handleInputChange}
              name='dateOfBirth'
              className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
            />
            {mandatoryFieldError.dateOfBirth && <p style={{ color: 'red' }}>Please enter date of birth</p>}
          </div>
          <div>
            <label className='block font-medium text-gray-700 mb-1'>
              Nationality<i className='text-red-600'>*</i>
            </label>
            <CountryList
              disabled={readOnly}
              width={'w-11/12'}
              value={formData.nationality}
              handleInputChange={handleInputChange}
              name='nationality'
            />
            {mandatoryFieldError.nationality && <p style={{ color: 'red' }}>Please select nationality</p>}
          </div>
        </div>

        {/* 6th Row */}
        <div className='grid grid-cols-2 gap-4 mb-4'>
          <div>
            <label htmlFor='address' className='block font-medium text-gray-700 mb-1'>
              Residence Address<i className='text-red-600'>*</i>
            </label>
            <input
              maxLength={800}
              disabled={readOnly}
              type='text'
              id='address'
              placeholder='Enter Address'
              value={formData.address}
              onChange={handleInputChange}
              name='address'
              className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
            />
            {mandatoryFieldError.address && <p style={{ color: 'red' }}>Please enter address</p>}
          </div>

          <div>
            <label htmlFor='fileInput' className='block font-medium text-gray-700 my-1.5'>
              Upload Proof of Address<i className='text-red-600'>*</i>
            </label>
            {formData.addressProofURL && (
              // if im accessing while logged in, is ok cuz cookie will settle.
              // if im accessing while as guest, i have to pull the token from query
              <>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/get-upload?fileName=${formData.addressProofURL.split('/').slice(-1)[0]}&accessToken=${token}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-600 mr-2'
                >
                  {decodeURIComponent(formData.addressProofURL.split('/').slice(-1)[0])}
                </a>
                <span
                  className='cursor-pointer'
                  onClick={() => handleFileRemove('addressProofURL', addressProofURLRef)}
                >
                  {' '}
                  ❌{' '}
                </span>
              </>
            )}
            <input
              maxLength={800}
              disabled={readOnly}
              ref={addressProofURLRef}
              type='file'
              id='fileInput'
              name='addressProofURL'
              onChange={e => handleFileUpload(e, 'addressProofURL')}
            />
            {mandatoryFieldError.addressProofURL && <p style={{ color: 'red' }}>Please upload proof of address</p>}
          </div>
        </div>

        {/* 7th row */}
        <div className='grid grid-cols-2 gap-4 mb-4'>
          {/* <div>
          <label htmlFor="residenceStatus" className="block font-medium text-gray-700 mb-1">Residence Status</label>
          <select id="residenceStatus" value={formData.residenceStatus} onChange={handleInputChange} name='residenceStatus'className="w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500">
            <option value=""> - Please Select - </option>
            <option value="singaporean">Singaporean</option>
            <option value="permanentResident">Permanent Resident</option>
            <option value="foreigner">Foreigner</option>
            <option value="workPermit">Work Permit</option>
          </select>
          {formData.residenceStatus == "workPermit" ? (
            <select value={formData.workPermitType} onChange={handleInputChange} name='workPermitType' className="w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500">
              <option value=""> - Please Select - </option>
              <option value="DP">DP</option>
              <option value="EP">EP</option>
              <option value="PEP">PEP</option>
              <option value="entryPass">Entry Pass</option>
            </select>
          ) : ""}
          {formData.residenceStatus == "foreigner" ? (
            <div>
              <label htmlFor="fileInput" className="block font-medium text-gray-700 mb-1.5">Upload Foreigner Proof Of Address</label>
              {formData.foreignerProofOfAddressFile && ( <a href={formData.foreignerProofOfAddressFile} className='text-blue-600 mr-2'> {formData.foreignerProofOfAddressFile.split('/').slice(-1)[0]} </a> )}
              <input maxLength={800} disabled={readOnly} type="file" id="fileInput" name="foreignerProofOfAddressFile" onChange={(e) => handleFileUpload(e, 'foreignerProofOfAddressFile')} className='' />
            </div>
          ) : ""}
        </div> */}
          <div>
            <label className='block font-medium text-gray-700 mb-1'>
              Country of Residence<i className='text-red-600'>*</i>
            </label>
            <CountryList
              disabled={readOnly}
              width={'w-11/12'}
              value={formData.residenceCountry}
              handleInputChange={handleInputChange}
              name='residenceCountry'
            />
            {mandatoryFieldError.residenceCountry && <p style={{ color: 'red' }}>Please enter Country of Residence</p>}
          </div>
          <div>
            <label htmlFor='occupation' className='block font-medium text-gray-700 mb-1'>
              Occupation
            </label>
            <input
              maxLength={800}
              disabled={readOnly}
              type='text'
              id='occupation'
              placeholder='Enter Occupation'
              onChange={handleInputChange}
              name='occupation'
              value={formData?.occupation}
              className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
            />
          </div>

          <div>
            <p className='flex'>
              <span className='mr-2 mt-1'> Source of Wealth </span>
              <Modal
                title='Definitions'
                token={token}
                CustomButton={() => <CustomTooltip title='Click to View definition/guidance' />}
              >
                <SourceOfWealth />
              </Modal>
            </p>
            <textarea
              maxLength={800}
              disabled={readOnly}
              placeholder='Enter Source of Wealth'
              onChange={handleInputChange}
              name='sourceOfWealth'
              value={formData.sourceOfWealth}
              className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
              rows={3}
            />
          </div>

          <div>
            <div className='flex'>
              <span className='mr-2 mt-1'> Source of Funds </span>
              <Modal
                title='Definitions'
                token={token}
                CustomButton={() => <CustomTooltip title='Click to View definition/guidance' />}
              >
                <SourceOfFunds />
              </Modal>
            </div>
            <textarea
              maxLength={800}
              disabled={readOnly}
              placeholder='Enter Source of Funds'
              onChange={handleInputChange}
              name='sourceOfFunds'
              value={formData.sourceOfFunds}
              className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
              rows={3}
            />
          </div>
        </div>

        {/* 8th row */}
        <div className='grid grid-cols-2 gap-4 mb-4'>
          {/* <div>
          <label htmlFor="secondNationality" className="block font-medium text-gray-700 mb-1">Second Nationality</label>
          <select id="secondNationality" value={formData.secondNationality} onChange={handleInputChange} name='secondNationality'className="w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500">
            <option value="singaporean">Singaporean</option>
            <option value="permanentResident">Permanent Resident</option>
          </select>
        </div> */}

          {decodedToken.companyId == 'Inspro_Insurance' && (
            <div>
              <label htmlFor='Account Manager' className='block font-medium text-gray-700 mb-1'>
                Account Manager
              </label>
              <input
                maxLength={800}
                disabled={readOnly}
                id='Account Manager'
                placeholder='Enter Account Manager'
                name='accountManager'
                value={formData.accountManager}
                onChange={handleInputChange}
                className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
              />
            </div>
          )}
        </div>

        {/* 9th row - Other Documents */}
        <div className='grid grid-cols-2 gap-4 mb-4'>
          <div>
            <p> Other Document (Eg Organisation Chart, ACRA) </p>
            {formData?.otherDocuments && (
              <>
                {Array.isArray(formData.otherDocuments) ? (
                  // 如果是数组，显示所有文件
                  formData.otherDocuments.map((file, index) => (
                    <div key={index} className='mb-2'>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/get-upload?fileName=${file.split('/').slice(-1)[0]}&accessToken=${token}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-blue-600 mr-2'
                      >
                        {decodeURIComponent(file.split('/').slice(-1)[0])}
                      </a>
                      <span
                        className='cursor-pointer'
                        onClick={() => handleFileRemoveFromArray('otherDocuments', index)}
                      >
                        {' '}
                        ❌{' '}
                      </span>
                    </div>
                  ))
                ) : (
                  // 如果是字符串，显示单个文件
                  <div>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/get-upload?fileName=${formData.otherDocuments.split('/').slice(-1)[0]}&accessToken=${token}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-blue-600 mr-2'
                    >
                      {decodeURIComponent(formData?.otherDocuments.split('/').slice(-1)[0])}
                    </a>
                    <span className='cursor-pointer' onClick={() => handleFileRemove('otherDocuments', null)}>
                      {' '}
                      ❌{' '}
                    </span>
                  </div>
                )}
              </>
            )}
            <label className='cursor-pointer bg-gray-200 px-1 py-0.5 mt-1 rounded'>
              Browse
              <input
                maxLength={800}
                disabled={readOnly}
                type='file'
                name='otherDocuments'
                onChange={e => handleFileUpload(e, 'OtherDocumentLink')}
                className='hidden'
              />
            </label>
          </div>
        </div>

        {/* 10th row */}
        <div className='grid grid-cols-2 gap-4 mb-4'>
          <div className={formData.pepPerson === 'yes' ? 'border p-2' : 'p-2'}>
            <div className='block'>
              <label className='font-medium text-gray-700'>
                Are you a current or former PEP?<i className='text-red-600 ml-1 mr-1'>*</i>
              </label>
              <Modal
                title='Definitions'
                token={token}
                className='inline'
                CustomButton={() => <CustomTooltip title='Click to View definition/guidance' />}
              >
                <CurrentPEP />
              </Modal>
            </div>
            <div className='mt-1 flex items-center'>
              <input
                maxLength={800}
                disabled={readOnly}
                type='radio'
                id='currentPEP'
                name='pepPerson'
                value='yes'
                onChange={handleInputChange}
                checked={formData?.pepPerson === 'yes'}
                className='mr-1 w-4 h-4'
              />
              <label htmlFor='currentPEP' className='mr-2'>
                Yes
              </label>
              <input
                maxLength={800}
                disabled={readOnly}
                type='radio'
                id='formerPEP'
                name='pepPerson'
                value='no'
                onChange={handleInputChange}
                checked={formData?.pepPerson === 'no'}
                className='mr-1 w-4 h-4'
              />
              <label htmlFor='formerPEP'>No</label>
            </div>

            {formData.pepPerson === 'yes' && (
              <div className='mt-1 '>
                <CustomTooltip
                  className='block'
                  title='Please describe the nature of your Political Exposure, including your official title or role, the government/institution you are affiliated with, the country of service, and the period of involvement. e.g. Former Deputy Minister, Ministry of Health, Malaysia (2015–2020)'
                />
                <textarea
                  maxLength={800}
                  disabled={readOnly}
                  name='pepPersonText'
                  placeholder='Enter Details of PEP'
                  onChange={handleInputChange}
                  className='w-11/12 mb-3 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                  rows={3}
                >
                  {formData?.pepPersonText ||
                    `${formData?.pepPersonCountryOrOrganisation || ''}${formData?.pepPersonPublicFunctionNature || ''}${formatDateToString(formData?.pepPersonRoleFrom) || ''}${formatDateToString(formData?.pepPersonRoleTo) || ''}`}
                </textarea>
                {/* The above fields are to show records that were created in the past before changing to "formData?.pepPersonText"*/}

                {/* <label className="block font-medium text-gray-700"> Country/Organisation which PEP holds prominent public function</label>
                    <input maxLength={800} disabled={readOnly} type="text" name="pepPersonCountryOrOrganisation"  value={formData?.pepPersonCountryOrOrganisation} placeholder="Enter Country/Organisation " onChange={handleInputChange}  className="w-11/12 p-1.5 mb-3 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500"/> <br/>
                    <label className="block font-medium text-gray-700"> Nature of prominent public function </label>
                    <input maxLength={800} disabled={readOnly} type="text" name="pepPersonPublicFunctionNature" value={formData?.pepPersonPublicFunctionNature}  placeholder="Enter Nature of prominent public function" onChange={handleInputChange}  className="w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/> <br/>

                    <label className="font-medium text-gray-700"> From: </label>
                    <input maxLength={800} disabled={readOnly} type="date" name="pepPersonRoleFrom" value={formData?.pepPersonRoleFrom} onChange={handleInputChange} id="date" className="w-1/8 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none my-1 focus:border-green-500" />
                    <label className="font-medium text-gray-700"> To </label>
                    <input maxLength={800} disabled={readOnly} type="date" name="pepPersonRoleTo" value={formData?.pepPersonRoleTo} onChange={handleInputChange} id="date" className="w-1/8 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500" /> */}
              </div>
            )}
            {mandatoryFieldError.pepPerson && <p style={{ color: 'red' }}>Please select PEP status</p>}
          </div>

          <div className={formData.pepAssociate === 'yes' ? 'border p-2' : 'p-2'}>
            <div className='block'>
              <label className='font-medium text-gray-700 mb-1'>
                Are you a close associate of any current or former PEP<i className='text-red-600 ml-1 mr-1'>*</i>
              </label>
              <Modal
                title='Definitions'
                token={token}
                className='inline'
                CustomButton={() => <CustomTooltip title='Click to View definition/guidance' />}
              >
                <AssociaterPEP />
              </Modal>
            </div>
            <div className='mt-1 flex items-center'>
              <input
                maxLength={800}
                disabled={readOnly}
                type='radio'
                id='pepYes'
                name='pepAssociate'
                value='yes'
                onChange={handleInputChange}
                checked={formData.pepAssociate === 'yes'}
                className='mr-1 w-4 h-4'
              />
              <label htmlFor='pepYes' className='mr-2'>
                Yes
              </label>
              <input
                maxLength={800}
                disabled={readOnly}
                type='radio'
                id='pepNo'
                name='pepAssociate'
                value='no'
                onChange={handleInputChange}
                checked={formData.pepAssociate === 'no'}
                className='mr-1 w-4 h-4'
              />
              <label htmlFor='pepNo'>No</label>
            </div>

            {formData.pepAssociate === 'yes' && (
              <div className='mt-1'>
                <CustomTooltip
                  className='block'
                  title='Please provide details of your relationship with the Politically Exposed Person (PEP), including their full name, official title or role, government/institution affiliation, country of service, period of involvement, and your relationship to them (e.g. spouse, business partner, sibling). e.g. Spouse of current Minister of Finance, Indonesia (2021–present)'
                />
                <textarea
                  maxLength={800}
                  disabled={readOnly}
                  name='pepAssociateText'
                  placeholder='Enter Details of PEP'
                  onChange={handleInputChange}
                  className='block w-11/12 mb-3 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                  rows={3}
                >
                  {`${formData?.pepAssociateCountryOrOrganisation || ''}${formData?.pepAssociatePublicFunctionNature || ''}${formatDateToString(formData?.pepAssociateRoleFrom) || ''}${formatDateToString(formData?.pepAssociateRoleTo) || ''}${formData?.pepAssociateText || ''}`}
                </textarea>
                {/* <p className='font-medium'> Relationship with PEP </p>
                    <input maxLength={800} disabled={readOnly} type="text" name="pepAssociateRelationship" value={formData?.pepAssociateRelationship} placeholder="Enter relationship with PEP " onChange={handleInputChange}  className="w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/>
                    <p className='font-medium'> PEP Name </p>
                    <input maxLength={800} disabled={readOnly} type="text" name="pepAssociateName" value={formData?.pepAssociateName} placeholder="Enter PEP Name" onChange={handleInputChange}  className="w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/>
                    <p className='font-medium'> Country/Organisation which PEP holds prominent public function </p>
                    <input maxLength={800} disabled={readOnly} type="text" name="pepAssociateCountryOrOrganisation" value={formData?.pepAssociateCountryOrOrganisation} placeholder="Enter Country/Organisation " onChange={handleInputChange}  className="w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/>
                    <p className='font-medium'> Nature of prominent public function </p>
                    <input maxLength={800} disabled={readOnly} type="text" name="pepAssociatePublicFunctionNature" value={formData?.pepAssociatePublicFunctionNature} placeholder="Enter Nature of prominent public function" onChange={handleInputChange}  className="w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/>

                    <span className='font-medium'> From: </span>
                    <input maxLength={800} disabled={readOnly} type="date" id="date" name="pepAssociateRoleFrom" value={formData?.pepAssociateRoleFrom} onChange={handleInputChange} className="w-1/8 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500" />
                    <span className='font-medium'> To </span>
                    <input maxLength={800} disabled={readOnly} type="date" id="date" name="pepAssociateRoleTo" value={formData?.pepAssociateRoleTo} onChange={handleInputChange} className="w-1/8 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500" /> */}
              </div>
            )}
            {mandatoryFieldError.pepAssociate && <p style={{ color: 'red' }}>Please select PEP associate status</p>}
          </div>
        </div>
        {/* 11th row */}
        <div className='grid grid-cols-2 gap-4 mb-4'>
          <div className={formData.pepFamily === 'yes' ? 'border p-2' : 'p-2'}>
            <div className='block'>
              <label className='font-medium text-gray-700'>
                Are you a family member of any current or former PEP?<i className='text-red-600 ml-1 mr-1'>*</i>
              </label>
              <Modal
                title='Definitions'
                token={token}
                className='inline'
                CustomButton={() => <CustomTooltip title='Click to View definition/guidance' />}
              >
                <FamilyMemberPEP />
              </Modal>
            </div>
            <div className='mt-1 flex items-center'>
              <input
                maxLength={800}
                disabled={readOnly}
                type='radio'
                id='familyYes'
                name='pepFamily'
                value='yes'
                onChange={handleInputChange}
                checked={formData.pepFamily === 'yes'}
                className='mr-1 w-4 h-4'
              />
              <label htmlFor='familyYes' className='mr-2'>
                Yes
              </label>
              <input
                maxLength={800}
                disabled={readOnly}
                type='radio'
                id='familyNo'
                name='pepFamily'
                value='no'
                onChange={handleInputChange}
                checked={formData.pepFamily === 'no'}
                className='mr-1 w-4 h-4'
              />
              <label htmlFor='familyNo'>No</label>
            </div>

            {formData.pepFamily === 'yes' && (
              <div className='mt-1'>
                <CustomTooltip
                  className='block'
                  title='Please provide details of your relationship with the Politically Exposed Person (PEP), including their full name, official title or role, government/institution affiliation, country of service, period of involvement, and your relationship to them (e.g. spouse, business partner, sibling). e.g. Spouse of current Minister of Finance, Indonesia (2021–present)'
                />
                <textarea
                  maxLength={800}
                  disabled={readOnly}
                  name='pepFamilyText'
                  placeholder='Enter Details of PEP'
                  onChange={handleInputChange}
                  className='w-11/12 mb-3 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                  rows={3}
                >
                  {`${formData?.pepFamilyCountryOrOrganisation || ''}${formData?.pepFamilyPublicFunctionNature || ''}${formatDateToString(formData?.pepFamilyRoleFrom) || ''}${formatDateToString(formData?.pepFamilyRoleTo) || ''}${formData?.pepFamilyText || ''}`}
                </textarea>
                {/* <p className='font-medium'> Relationship with PEP  </p>
                    <input maxLength={800} disabled={readOnly} type="text" name="pepFamilyRelationship" value={formData?.pepFamilyRelationship}  placeholder="Enter relationship with PEP " onChange={handleInputChange}  className="w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/> <br/>
                    <p className='font-medium'> PEP Name </p>
                    <input maxLength={800} disabled={readOnly} type="text" name="pepFamilyName" value={formData?.pepFamilyName}  placeholder="Enter PEP Name" onChange={handleInputChange}  className="w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/> <br/>

                    <p className='font-medium'> Country/Organisation which PEP holds prominent public function  </p>
                    <input maxLength={800} disabled={readOnly} type="text" name="pepFamilyCountryOrOrganisation" value={formData?.pepFamilyCountryOrOrganisation}  placeholder="Enter Country/Organisation " onChange={handleInputChange}  className="w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/> <br/>
                    <p className='font-medium'>Nature of prominent public function  </p>
                    <input maxLength={800} disabled={readOnly} type="text" name="pepFamilyPublicFunctionNature" value={formData?.pepFamilyPublicFunctionNature}  placeholder="Enter Nature of prominent public function" onChange={handleInputChange}  className="w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/> <br/>

                    <span className='font-medium'> From:  </span>
                    <input maxLength={800} disabled={readOnly} type="date" id="date" name="pepFamilyRoleFrom"value={formData?.pepFamilyRoleFrom}   onChange={handleInputChange} className="w-1/8 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500" />
                    <span className='font-medium'> To  </span>
                    <input maxLength={800} disabled={readOnly} type="date" id="date" name="pepFamilyRoleTo" value={formData?.pepFamilyRoleTo}  onChange={handleInputChange} className="w-1/8 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500" /> */}
              </div>
            )}
            {mandatoryFieldError.pepFamily && <p style={{ color: 'red' }}>Please select PEP family status</p>}
          </div>

          {formAuthority === 'Law' ? (
            <div>
              <label className='font-medium text-gray-700'>
                Information on business relationship (Nature)<i className='text-red-600 ml-1 mr-1'>*</i>
              </label>
              <Modal
                title='Definitions'
                token={token}
                className='inline'
                CustomButton={() => <CustomTooltip title='Click to View definition/guidance' />}
              >
                <InformationPEP />
              </Modal>
              <textarea
                maxLength={800}
                disabled={readOnly}
                placeholder='Enter Information on Business Relationship'
                onChange={handleInputChange}
                name='businessRelationShip'
                value={formData.businessRelationShip}
                className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                rows={3}
              />
              {mandatoryFieldError.businessRelationShip && (
                <p style={{ color: 'red' }}>Please upload proof of address</p>
              )}
            </div>
          ) : (
            <div>
              <label className='font-medium text-gray-700'>Notes</label>
              <textarea
                maxLength={800}
                disabled={readOnly}
                placeholder='Enter Notes'
                onChange={handleInputChange}
                name='notes'
                value={formData.notes}
                className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                rows={3}
              />
            </div>
          )}
          {/* {formAuthority === 'Law' && (
              <div>
                <label className="block font-medium text-gray-700">IWhether to enable video verification?</label>
                <div className="mt-1 flex items-center">
                  <input maxLength={800} disabled={readOnly} type="radio" id="isvVideoYES" name="isvVideo" value="yes" onChange={handleInputChange} checked={formData?.isvVideo === 'yes'} className="mr-1 w-4 h-4" />
                  <label htmlFor="isvVideoYES"  className="mr-2">Yes</label>
                  <input maxLength={800} disabled={readOnly} type="radio" id="isvVideoNO" name="isvVideo" value="no" onChange={handleInputChange} checked={formData?.isvVideo === 'no'} className="mr-1 w-4 h-4"/>
                  <label htmlFor="isvVideoNO">No</label>
                </div>
              </div>

           )} */}
        </div>

        {/* Counterparties and Notes Section - Only for Law version */}
        {(formAuthority === 'Law' || formAuthority === 'law') && (
          <div className='grid grid-cols-2 gap-4 mb-4'>
            {/* Counterparties Section */}
            <div>
              {isDataLoaded && (
                <CounterpartyManager
                  formAuthority={formAuthority}
                  readOnly={readOnly}
                  initialCounterparties={formData.counterparties || []}
                  initialEnableCounterparties={formData.enableCounterparties || 'no'}
                  onCounterpartiesChange={handleCounterpartiesChange}
                  token={token}
                  currentUrl={currentUrl}
                />
              )}
            </div>

            {/* Notes Section - Only for Law version */}
            <div>
              <label className='font-medium text-gray-700'>Notes</label>
              <textarea
                maxLength={800}
                disabled={readOnly}
                placeholder='Enter Notes'
                onChange={handleInputChange}
                name='notes'
                value={formData.notes}
                className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                rows={3}
              />
            </div>
          </div>
        )}

        <br />
        <hr />
        <br />
        {currentUrl !== '/form/kyc-aml' && !readOnly && (
          <div className='my-4 flex justify-between'>
            <button
              onClick={handleSaveForm}
              disabled={readOnly}
              className='ml-4 bg-green-500 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-600'
            >
              Save Form
            </button>

            {showSubmitButton && (
              <p>
                <Tooltip
                  title={
                    <p>
                      {' '}
                      This sends an email with a url to view this form in an external page, to the email field listed
                      above. <br /> After Sending Notification Email, this form can only be submitted via the external
                      form link.
                    </p>
                  }
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: { maxWidth: '445px', fontSize: '14px' },
                    },
                  }}
                >
                  <span className='text-xl relative bottom-0.5'> ⓘ </span>
                </Tooltip>

                <button
                  onClick={handleSendEmail}
                  disabled={readOnly}
                  className='ml-4 bg-green-500 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-600'
                >
                  Send Notification Email
                </button>

                {!formData?.emailSent ? (
                  <button
                    onClick={handleSubmitForm}
                    disabled={readOnly}
                    className='ml-4 bg-green-500 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-600'
                  >
                    Submit
                  </button>
                ) : (
                  <Tooltip
                    title={<p style={{ fontSize: '15px' }}>Email Notification sent, awaiting customer submission.</p>}
                    arrow
                    className='ml-4 bg-green-300 text-white font-semibold py-2 px-4 rounded-md '
                  >
                    <button onClick={handleSubmitForm} disabled={false} className='ml-4'>
                      {' '}
                      Submit{' '}
                    </button>
                  </Tooltip>
                )}
              </p>
            )}
          </div>
        )}

        {currentUrl === '/form/kyc-aml' && !readOnly && (
          <div className='my-4 flex justify-between'>
            {/* <button onClick={handleSaveForm} className='ml-4 bg-green-500 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-600'>
            Save Form
          </button> */}
            {showSubmitButton && (
              <p>
                {/* <button onClick={handleSubmitForm} className='ml-4 bg-green-500 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-600'>
                Submit
              </button> */}
                <button
                  onClick={handleSaveForm}
                  className='ml-4 bg-green-500 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-600'
                >
                  Save Form
                </button>
              </p>
            )}
          </div>
        )}
      </div>
      <br />
      <br /> <br />
    </div>
  );
};

export default _IndividualDetails;

const SourceOfWealth = () => {
  return (
    <div className='p-6 w-6xl'>
      <p>
        <strong> Source of wealth: </strong> Origin of the customer’s/beneficial owner’s entire body of wealth (total
        assets) and how it was acquired. Although you may not have information about assets that are not
        deposited/processed by you, it may be possible to obtain information from the customer, commercial databases or
        other open sources (evidence of title, copies of trust deeds, audited accounts, salary details, tax returns,
        bank statements etc.)
      </p>
    </div>
  );
};

const SourceOfFunds = () => {
  return (
    <div className='p-6 w-6xl'>
      <p>
        <strong> Source of funds: </strong> Origin of the funds or other assets which are the subject of the
        establishment of business relations (amount invested in/transferred to you etc.). You should not limit source of
        funds inquiry to the direct source from which the funds have been transferred, but also the activity that
        generated the funds. The information should be substantive and facilitate the establishment of the provenance of
        the funds (Salary payment, sale proceeds etc.)
      </p>
    </div>
  );
};

const CurrentPEP = () => {
  return (
    <div className='p-6 w-6xl'>
      <p>
        <strong>A Politically Exposed Person (PEP)</strong> refers to someone who is or has been entrusted with a
        prominent public function that includes the role held by a head of state, a head of government, a senior
        politician, a senior government, judicial or military official, a senior executive of a state-owned corporation
        or a senior political party official either domestically or overseas which encompasses of:(a) a foreign
        politically-exposed individual; (b) a domestic politically exposed individual; or (c) an individual who has been
        entrusted with a prominent function in an international organisation. In relation to an international
        organisation, means the role held by a member of the senior management of the international organisation
        (including a director, deputy director or member of a board of the international organisation, or an equivalent
        appointment in the international organisation).
      </p>
    </div>
  );
};

const FamilyMemberPEP = () => {
  return (
    <div className='p-6 w-6xl'>
      <p>
        <strong>A family member</strong> refers to an individual who is a spouse, child (including an adopted child or a
        stepchild), sibling or parent of the politically-exposed individual.
      </p>
    </div>
  );
};
const AssociaterPEP = () => {
  return (
    <div className='p-6 w-6xl'>
      <p>
        <strong>A close associate</strong> refers to an individual who is known to be closely connected to the
        politically-exposed individual, either socially or professionally, such as, but not limited to — (a) a partner
        of the politically exposed individual; (b) an employee or employer of the politically exposed individual; (c) a
        person accustomed or under an obligation, whether formal or informal, to act in accordance with the directions,
        instructions or wishes of the politically exposed individual; or (d) a person whose directions, instructions or
        wishes the politically exposed individual is accustomed or under an obligation, whether formal or informal, to
        act in accordance with.
      </p>
    </div>
  );
};
const InformationPEP = () => {
  return (
    <div className='p-6 w-6xl'>
      <p>
        <strong>A business relationship</strong> refers to the purpose and intended nature of client relationship
        between the parties. Please describe the nature of services that is intended to be established. For example: •
        To receive legal advice on a property purchase. • To set up and manage a family trust. • To process payments for
        export and import of goods. • To obtain company secretarial services for my new business. • To hold client funds
        for a litigation settlement.
      </p>
    </div>
  );
};

//  {/* <h4 className="text-lg font-semibold text-center">DECLARATION AS TO TAX COMPLIANCE</h4> <br/>
//       <p className="font-semibold">I, the below named person, being a/the beneficial owner/account signatory of the Entity named below (“the  Entity”) incorporated  in  the  Relevant Jurisdiction  named  below  HEREBY CONFIRM   AND DECLARE AS FOLLOWS: </p> <br/>
//       <ol className="list-decimal ml-6">
//         <li>
//           I am aware of Singapore’s commitment to safeguarding its financial system from being used to harbour orlaunder tax evasion monies or proceeds from serious tax offences with effect from 1 July 2013. Serious tax crimes include omissions, falsifications or fraudulent conduct perpetrated with willful intent to evade tax or to assist others in evading tax
//         </li> <br/>
//         <li>
//         I have sought/shall ensure that the Entity seeks appropriate legal and tax advice and/or fully considers it's legal and tax position with regards to the setting up of the Entity, its holding structure and its tax and other regulatory reporting obligations in all applicable countries;
//         </li> <br/>
//         <li>
//           I have sought appropriate legal and tax advice on and/or am aware and have fully considered my tax and other regulatory reporting obligations in all applicable countries with regards to my interest in the Entity and I undertake to comply with these obligations;
//         </li> <br/>
//         <li>
//           All funds which are to be injected into the Entity whether as loans or equity are sourced from legitimate sources;
//         </li> <br/>
//         <li>
//           Where such funds are required to be reported for tax purposes to anyrelevant revenue authorities, therelevant reporting requirements have been met and unless disputed, alltaxes, if any, which are payable on such funds have been paid when due;
//         </li> <br/>
//         <li>
//           To the best of my knowledge,the Entity and its holding structure are not used in connection with anyserious tax offence, my tax affairs are fully compliant with all applicable laws and regulations and I have not been convicted of any serious tax offence in any country; and
//         </li> <br/>
//         <li>
//           To the best of my knowledge, the Entity and its holding structure are not used in connection with any serious tax offence, my tax affairs are fully compliant with all applicable laws and regulations and I have not been convicted of any serious tax offence in any country; and
//         </li> <br/>
//         <li>
//           I will notify you immediately if there is any change to the circumstances above.
//         </li> <br/> <br/>
//       </ol>

//       <p className="font-semibold">I UNDERSTAND THAT CAMBRIDGE ADVISERS:</p><br/>
//       <ul className="list-disc ml-6">
//         <li>
//           Will rely on the information I have provided above when considering this application for services; and
//         </li> <br/>
//         <li>
//           Is not responsible for providing me and/or the Entity with any legal or tax advice and I confirm that we have not relied on Cambridge Advisers to provide us with such advice.
//         </li> <br/>
//       </ul>
//       <p className='font-semibold'>DECLARATION:</p> <br/>
//       <ul className="list-disc ml-6">
//         <li>
//           <p> In submitting this form; I declare that all information given in this application and in the attached annexes (if any) is true and correct and that I have not willfully suppressed any material fact.</p> <br/>
//         </li>
//       </ul> */}
//        {/* First Declaration */}
//        {/* <div className="col-span-2 grid grid-cols-2 gap-4">
//         <div>
//           <label htmlFor="declarationName" className="block mb-2">Name:</label>
//           <input maxLength={800} disabled={readOnly} type="text" id="declarationName" placeholder="Enter name here" name='declarationName' value={formData?.declarationName} onChange={handleInputChange} className="w-full p-1.5 border-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500" />
//         </div>
//         <div>
//           <label htmlFor="date" className="block mb-2">Date:</label>
//           <input maxLength={800} disabled={readOnly} type="date" id="date" name='declarationDate' onChange={handleInputChange} value={formData?.declarationDate} className="w-full p-1.5 border-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500" />
//         </div>
//       </div> */}

//         {/* 2nd Declaration */}
//        {/* <div className="col-span-2 grid grid-cols-2 gap-4">
//         <div>
//           <p> Signature: </p>

//           <div className="border border-gray-400 bg-gray-100  mb-3 p-2 ">
//             <SignatureCanvas
//                 penColor='black'
//                 canvasProps={{ width: 525, height: 200  }}
//                 ref={signatureRef}
//             />
//             <div className='flex justify-between'>
//                 <button onClick={handleClear} className='bg-slate-300 hover:bg-slate-400 text-black font-medium py-1 px-5 rounded-lg'>Clear</button>
//             </div>
//           </div>
//         </div>
//         <div>
//           <p className="m-0.5"> Saved Signature: </p>
//           <img src={formData?.signatureUrl || ""} className="border h-60 rounded-md m-1 border-gray-600"/>
//         </div>
//       </div> */}
